
import { catchError, map } from 'rxjs/operators';
import { Observable, of, EMPTY } from 'rxjs';
import { StandardAppConfig } from '../Configuration';
import {
    abrHttpClient,
    AbrHttpClient,
    abrOAuthService,
    cookieOAuthService,
    mockAbrHttpClient,
    mockOAuthService,
    sessionStorageOAuthService
} from './snsw-business-lookup-interop';

export interface SnswBusinessLookupEnvironment {
    authMode: 'COOKIE' | 'SESSION_STORAGE' | 'MOCK';
    abrApiEndpoint: string;
    abrAcnEndpoint: string;
    abrAbnEndpoint: string;
    abrNameEndpoint: string;
}

export interface CharityDetails {
    acncRegistrationFrom?: string;
    charityTypeDescription?: string;
}

export interface BusinessDetails {
    businessName?: string;
    abn?: string;
    entityType?: string;
    entityName?: string;
    gstRegistrationStatus?: boolean;
    gstRegistrationFrom?: string; // LocalDate

    // Transinient
    entityDescription?: string;
    gstRegistrationTo?: string; // should be an ISO formatted date time
    abnEffectiveFrom?: string; // should be an ISO formatted date time
    abnEffectiveTo?: string;
    abnEntityStatusCode?: string;
    label?: string;
    businessTypeCode?: string; // code for BI analytics
    businessType?: string;
    charityDetails?: CharityDetails;
    anzsicCode?: string;
    mainBusinessAddressPostCode?: string;
    mainBusinessAddressState?: string;
}

export class SnswBusinessLookupService {
    public environment: SnswBusinessLookupEnvironment;

    constructor(
        appConfig: StandardAppConfig
    ) {
        const authGuid = appConfig.businessSearchConfig?.authenticationGuid;
        // only add the auth queryParam if it is defined (the Middle Tier might add it as it proxies)
        const auth = authGuid ? `&authenticationGuid=${authGuid}` : '';
        this.environment = {
            authMode: appConfig.businessSearchConfig?.authMode!!,
            abrApiEndpoint: appConfig.businessSearchConfig?.baseUrl!!,
            abrAbnEndpoint:
                `/abrsearchbyabn?searchString={searchstring}&includeHistoricalDetails=N${auth}`,
            abrAcnEndpoint:
                `/abrsearchbyacn?searchString={searchstring}&includeHistoricalDetails=N${auth}`,
            abrNameEndpoint:
                `/abrsearchbyname?name={searchstring}&activeABNsOnly=Y&maxSearchResults=10${auth}`,
        };
        if (appConfig?.appConfig?.enableDebug) {
            console.log('Configuring ABR lookup with settings:', this.environment, appConfig.businessSearchConfig?.authMode);
        }
    }

    getHttpClient(): AbrHttpClient {
        // in practice, we need client credentials for the storybook, and cookie for the application, session storage is
        // really a legacy mode.
        switch (this.environment.authMode) {
            case 'COOKIE':
                return abrHttpClient;
            case 'SESSION_STORAGE':
                return abrHttpClient;
            case 'MOCK':
                return mockAbrHttpClient;
        }
    }

    getAuthService(): abrOAuthService {
        // in practice, we need client credentials for the storybook, and cookie for the application, session storage is
        // really a legacy mode.
        switch (this.environment.authMode) {
            case 'COOKIE':
                return cookieOAuthService;
            case 'SESSION_STORAGE':
                return sessionStorageOAuthService;
            case 'MOCK':
                return mockOAuthService;
        }
    }

    checkABR(searchStr: string): Observable<any> {
        let url = '';
        if (searchStr === undefined || searchStr === '')
        {
            return EMPTY; // to handle edge case: type abn data and then hit backspace all the way
        }

        if (this.isACN(searchStr)) {
            searchStr = searchStr.replace(/\s/g, '');
            url =
                this.environment.abrApiEndpoint +
                this.environment.abrAcnEndpoint.replace('{searchstring}', searchStr);
            return this.searchByACN(url);
        } else if (this.isABN(searchStr)) {
            searchStr = searchStr.replace(/\s/g, '');
            url =
                this.environment.abrApiEndpoint +
                this.environment.abrAbnEndpoint.replace('{searchstring}', searchStr);
            return this.searchByABN(url);
        } else {
            url =
                this.environment.abrApiEndpoint +
                this.environment.abrNameEndpoint.replace('{searchstring}', searchStr);
            return this.searchByName(url);
        }
    }

    searchByACN(url : string): Observable<any> {
        let businessDetailsList : Array<BusinessDetails> = [];
        const httpOptions = {
            headers: this.getPCFHeaders()
        };

        return this.getHttpClient()
            .get(url, httpOptions)
            .pipe(
                map((result: any) => {
                    businessDetailsList = this.populateBusinessList(result, false);
                    return businessDetailsList;
                }),
                catchError(error => {
                    console.error(`lookupAbnAcn Data error=${JSON.stringify(error)}`);
                    businessDetailsList.length = 0;
                    return of(businessDetailsList);
                })
            );
    }

    searchByABN(url: string): Observable<any> {
        let businessDetailsList: Array<BusinessDetails> = [];
        const httpOptions = {
            headers: this.getPCFHeaders()
        };

        return this.getHttpClient().get(url, httpOptions)
            .pipe(
                map((result: any) => {
                    businessDetailsList = this.populateBusinessList(result, true);
                    return businessDetailsList;
                }),
                catchError(error => {
                    console.error(`lookupAbnAcn Data error=${JSON.stringify(error)}`);
                    businessDetailsList.length = 0;
                    return of(businessDetailsList);
                })
            );
    }

    private populateBusinessList(result: any, isABNSearch: boolean): Array<BusinessDetails> {
        let abrData: any;
        let businessName: string;
        let entityName: string;
        const businessDetailsList: Array<BusinessDetails> = [];

        if (isABNSearch) {
            abrData = result.SearchByABNv201408Response.SearchByABNv201408Result.Response.ResponseBody;
        } else {
            abrData = result.SearchByASICv201408Response.SearchByASICv201408Result.Response.ResponseBody;
        }

        if (abrData.Description) {
            // exception - no records found
            businessDetailsList.length = 0;
        } else {
            const entityType = this.getProp(
                () => abrData.EntityType.EntityTypeCode,
                ''
            );
            const entityDescription = this.getProp(
                () => abrData.EntityType.EntityDescription,
                ''
            );
            const abnEntityStatus = this.getProp(() => abrData.EntityStatus.Item.EntityStatusCode, undefined);
            const abnEffectiveFrom = this.getProp(() => abrData.EntityStatus.Item.EffectiveFrom, undefined);

            // charity
            const acncEffectiveFrom = this.getProp(() => abrData.ACNCRegistration.Item.EffectiveFrom, undefined);
            const acncStatus = this.getProp(() => abrData.ACNCRegistration.Item.Status, undefined);
            const charityType = this.getProp(() => abrData.CharityType.Item.CharityTypeDescription, undefined);

            if (entityType === 'IND') {
                const businessNameArray: string[] = [];

                businessNameArray.push(this.getProp(() => abrData.Name.Item.GivenName, ''));
                businessNameArray.push(this.getProp(() => abrData.Name.Item.OtherGivenName, ''));
                businessNameArray.push(this.getProp(() => abrData.Name.Item.FamilyName, ''));
                businessName = businessNameArray.filter(v => v.length > 0).join(' ');
                entityName = businessName;
            } else {
                businessName = this.firstNonNull(
                    () => this.extractMainTradingName(abrData),
                    () => this.extractLegalName(abrData),
                    () => this.extractMainName(abrData)) || '';
                entityName = this.firstNonNull(() => this.extractLegalName(abrData),() => this.extractMainName(abrData)) || '';
            }

            if (businessName === null || typeof businessName === 'undefined') {
                console.warn(`ABR business name extraction failed - entity type is ${entityType}`);
            }

            const abn = Array.isArray(abrData.ABN.Item) ? abrData.ABN.Item[0].IdentifierValue : abrData.ABN.Item.IdentifierValue;

            const gstItem = abrData.GoodsAndServicesTax?.Item;
            const gstRegistrationFrom = this.getProp(() => gstItem.EffectiveFrom, undefined);
            const gstRegistrationTo = this.getProp(() => gstItem.EffectiveTo, undefined);
            const gstRegistrationStatus = !!gstRegistrationFrom;
            const mainBusinessAddressState = this.getProp(() => abrData.MainBusinessPhysicalAddress.Item.StateCode, undefined);
            const mainBusinessAddressPostCode = this.getProp(() => abrData.MainBusinessPhysicalAddress.Item.Postcode, undefined);

            // get business state and postcode

            businessDetailsList.push({
                abn: Number(abn).toString(),
                entityType,
                entityDescription,
                businessName,
                entityName,
                gstRegistrationStatus,
                gstRegistrationFrom,
                gstRegistrationTo,
                abnEffectiveFrom,
                abnEntityStatusCode: abnEntityStatus,
                label: `${businessName} (ABN ${abn}) `,
                businessType: undefined,
                businessTypeCode: undefined,
                mainBusinessAddressPostCode: mainBusinessAddressPostCode,
                mainBusinessAddressState: mainBusinessAddressState,
                charityDetails: (acncEffectiveFrom || charityType) ? {
                    acncRegistrationFrom: acncStatus === 'Registered' ? acncEffectiveFrom : undefined,
                    charityTypeDescription: charityType
                } : undefined
            });
        }

        return businessDetailsList;
    }

    /**
     * Calls the ABR web service to lookup company details by company name.
     */
    searchByName(url: string): Observable<any> {
        const businessDetailsList : Array<BusinessDetails> = [];

        const httpOptions = {
            headers: this.getPCFHeaders()
        };

        return this.getHttpClient()
            .get(url, httpOptions)
            .pipe(
                map((result: any) => {
                    const abrData =
                        result.ABRSearchByNameAdvancedSimpleProtocol2017Response
                            .ABRSearchByNameAdvancedSimpleProtocol2017Result.Response
                            .ResponseBody;
                    if (abrData.Description) {
                        // exception - no records found
                        businessDetailsList.length = 0;
                    } else {
                        this.populateBusinessDetailsFromAbrData(abrData, businessDetailsList);
                    }
                    return businessDetailsList;
                }), catchError((error => {
                    console.error('error searching by name:', JSON.stringify(error));
                    businessDetailsList.length = 0;
                    return of(businessDetailsList);
                }))
            );
    }

    private populateBusinessDetailsFromAbrData(abrData: any, businessDetailsList: Array<BusinessDetails>) {
        // get array of results
        const resultsList = abrData.SearchResultsRecord?.Item;
        if (Array.isArray(resultsList)) {
            resultsList.forEach(business => {
                let orgName = this.getProp(() => business.Name.Item.FullName, '');
                if (!orgName) {
                    orgName = this.getProp(
                        () => business.Name.Item.OrganisationName,
                        ''
                    );
                }
                const entityType = this.getProp(
                    () => abrData.EntityType.EntityTypeCode,
                    ''
                );
                const abn = business.ABN.Item.IdentifierValue;
                businessDetailsList.push({
                    abn: Number(abn).toString(),
                    entityType,
                    businessName: orgName,
                    label: `${orgName} (ABN ${abn}) `
                });
            });
        } else {
            console.warn('Unexpected response from ABN name search : ', resultsList);
        }
    }

    /**
     * Checks if given value is an Australian Company Number (i.e. has 9 digits)
     */
    isACN(abnacn: string): boolean {
        return (
            this.isNumericOrSpace(abnacn) && abnacn.replace(/\s/g, '').length === 9
        );
    }

    /**
     * Checks if given value is an Australian Business Number (i.e. has 11 digits)
     */
    isABN(abnacn: string): boolean {
        return (
            this.isNumericOrSpace(abnacn) && abnacn.replace(/\s/g, '').length === 11
        );
    }

    isNumericOrSpace(abnacn: any): boolean {
        const numbersOrSpaceRegex = /^[0-9 ]+$/;
        return abnacn.match(numbersOrSpaceRegex);
    }

    /**
     * Safely access nested property within object
     */
    getProp(fn: any, defaultVal: any) : any {
        try {
            return fn();
        } catch (e) {
            return defaultVal;
        }
    }

    private getPCFHeaders() {
        const httpHeaderConfig = {
            'Accept': 'application/json'
        };
        // because here the auth token is added as a cookie, javascript cannot access that cookie
        // we need to fix this and route via middle tier so MT can add token and redirect and the auth token is
        // invisible to the JavaScript code (for stronger security)
        const authService = this.getAuthService();
        if (authService.hasValidAccessToken()) {
            // @ts-ignore
            const header = authService.authorizationHeader();
            if (header) {
                httpHeaderConfig['Authorization'] = authService.authorizationHeader();
            }
        } else {
            console.error('ABR: No Valid access token');
        }
        return httpHeaderConfig;
    }

    private firstNonNull<T>(...providers: (() => T)[]): T | null {
        for (const provider of providers) {
            const value: T = provider();
            if (!!value) {
                return value;
            }
        }

        return null;
    }

    private extractMainTradingName(abrData: any): string | null {
        return this.getProp(
            () => abrData.MainTradingName.Item.OrganisationName,
            null
        );
    }

    private extractLegalName(abrData: any): string | null {
        return this.getProp(
            () => abrData.LegalName.Item.OrganisationName,
            null
        );
    }

    private extractMainName(abrData: any): string | null {
        return this.getProp(
            () => abrData.Name.Item.OrganisationName,
            null
        );
    }
}
